import AdsRepository from './AdsRepository.js'

const LAST_SEARCH_KEY = 'LastSearch'
const LAST_ADS_SEARCHES_KEY = 'LastAdsSearches'
const LAST_DETAILS_VIEWED_KEY = 'LastDetailsViewed'

/**
 * @class
 * @implements AdsRepository
 */
export default class LocalStorageAdsRepository extends AdsRepository {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Storage} deps.localStorage
   * @param {AdsSearchParamsValueObject} deps.adsSearchParamsValueObjectFactory
   * @param {LastAdsSearchLegacyParamsMapper} deps.lastAdsSearchLegacyParamsMapperFactory
   * @param {Function} deps.lastSearchesDataToSearchesListValueObjectMapper
   * @param {Function} deps.lastDetailsViewedDataToLastDetailsViewedListValueObjectMapper
   * @param {Function} deps.log
   */
  constructor({
    localStorage,
    adsSearchParamsValueObjectFactory,
    lastAdsSearchLegacyParamsMapperFactory,
    lastSearchesDataToSearchesListValueObjectMapper,
    lastDetailsViewedDataToLastDetailsViewedListValueObjectMapper,
    log
  }) {
    super({
      localStorage,
      adsSearchParamsValueObjectFactory,
      lastAdsSearchLegacyParamsMapperFactory,
      lastSearchesDataToSearchesListValueObjectMapper,
      lastDetailsViewedDataToLastDetailsViewedListValueObjectMapper,
      log
    })

    this._localStorage = localStorage
    this._adsSearchParamsValueObjectFactory = adsSearchParamsValueObjectFactory
    this._lastAdsSearchLegacyParamsMapperFactory = lastAdsSearchLegacyParamsMapperFactory
    this._lastSearchesDataToSearchesListValueObjectMapper = lastSearchesDataToSearchesListValueObjectMapper
    this._lastDetailsViewedDataToLastDetailsViewedListValueObjectMapper =
      lastDetailsViewedDataToLastDetailsViewedListValueObjectMapper
    this._log = log
  }

  /**
   * @return {Object}
   */
  getLastAdsSearchParams() {
    this._log('Getting last ads search params')

    return new Promise((resolve, reject) => {
      try {
        const lastAdsSearchParams = this._localStorage.getItem(LAST_SEARCH_KEY)

        resolve(
          this._adsSearchParamsValueObjectFactory(
            lastAdsSearchParams
              ? this._lastAdsSearchLegacyParamsMapperFactory(JSON.parse(lastAdsSearchParams)).map()
              : {}
          ).toJSON()
        )
      } catch (error) {
        reject(error)
      }
    })
  }

  _getLastAdsSearchesFromStorage() {
    const lastSearchesDescriptor = {items: []}
    const lastSearchesDataString = this._localStorage.getItem(LAST_ADS_SEARCHES_KEY)
    return JSON.parse(lastSearchesDataString) || lastSearchesDescriptor
  }

  /**
   * @return {Object}
   */
  getLastAdsSearches() {
    const lastSearchesData = this._getLastAdsSearchesFromStorage()
    return this._lastSearchesDataToSearchesListValueObjectMapper.map(lastSearchesData)
  }

  async saveLastAdsSearch({listingSectionId, title, pageName, query, params}) {
    const LAST_ADS_SEARCHES_LIMIT = -20
    const {items} = this._getLastAdsSearchesFromStorage()

    items.push({listingSectionId, title, pageName, query, params})

    this._localStorage.setItem(LAST_ADS_SEARCHES_KEY, JSON.stringify({items: items.slice(LAST_ADS_SEARCHES_LIMIT)}))

    return true
  }

  _getLastDetailsViewedFromStorage() {
    const lastDetailsViewedDescriptor = {items: []}
    const lastDetailsViewedDataString = this._localStorage.getItem(LAST_DETAILS_VIEWED_KEY)
    return JSON.parse(lastDetailsViewedDataString) || lastDetailsViewedDescriptor
  }

  /**
   * @return {LastDetailsViewedList<ValueObject>}
   */
  getLastDetailsViewed() {
    const lastDetailsViewed = this._getLastDetailsViewedFromStorage()

    return this._lastDetailsViewedDataToLastDetailsViewedListValueObjectMapper.map(lastDetailsViewed.items)
  }

  saveLastDetailViewed({adId}) {
    const LAST_DETAILS_VIEWED_LIMIT = -10
    const {items} = this._getLastDetailsViewedFromStorage()

    const itemsWithoutDuplicates = Array.isArray(items) && items.filter(item => item.id !== adId)

    itemsWithoutDuplicates.push({id: adId})

    this._localStorage.setItem(
      LAST_DETAILS_VIEWED_KEY,
      JSON.stringify({
        items: itemsWithoutDuplicates.slice(LAST_DETAILS_VIEWED_LIMIT)
      })
    )

    return true
  }
}
